// src/components/notificaciones/BurbujaNotificaciones.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../services/firebase';
import notificationService from '../../services/notificationService';
import './BurbujaNotificaciones.css';
import {
  BellIcon, XMarkIcon, CheckIcon, CheckCircleIcon,
  InboxIcon, MapPinIcon, BookOpenIcon, UserIcon,
  SpeakerWaveIcon, SpeakerXMarkIcon, VozAltaIcon, VozMudaIcon,
  ChatBubbleLeftIcon, ExclamationTriangleIcon, XCircleIcon,
  NoSymbolIcon, LockClosedIcon, ArrowUturnLeftIcon, MegaphoneIcon,
  ClipboardDocumentListIcon, SparklesIcon
} from '../ui/Icons';
import { getModuloInfo } from '../../pages/menuConfig';
import DetalleNotificacion, { tieneDetalleListado } from '../ui/DetalleNotificacion';
import Interruptor from '../ui/Interruptor';
import voz from '../../utils/voz';

// Nombre legible del módulo origen: usa el label oficial de menuConfig si es un id de módulo;
// si no, formatea el slug (kebab/snake → Título) o lo deja como viene ("Crear Pedido", "Sistema").
const formatearModulo = (mod) => {
  if (!mod) return 'Sistema';
  const info = getModuloInfo(mod);
  if (info?.label) return info.label;
  if (/[-_]/.test(mod)) return mod.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  return mod;
};

function BurbujaNotificaciones({ puedeAbrirModulo }) {
  const navigate = useNavigate();
  // Candado de ruta (Layout.js, 09/10/2026): "Ver tarea" y "Ver chat" solo a quien puede abrir ese módulo; la
  // notificación le llega igual. Sin la función (pantalla vieja Dashboard.js, que no usa ninguna ruta), como antes.
  const puedeAbrir = (moduloId) => (typeof puedeAbrirModulo === 'function' ? puedeAbrirModulo(moduloId) : true);
  const [notificaciones, setNotificaciones] = useState([]);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [modalCerrando, setModalCerrando] = useState(false);
  // El sonido recuerda su estado por dispositivo (igual que la voz), así no hay
  // que volver a encenderlo en cada recarga.
  const [sonidoActivado, setSonidoActivado] = useState(() => {
    try { return localStorage.getItem('sonidoNotificaciones') === '1'; } catch { return false; }
  });
  // La voz recuerda su estado por dispositivo (localStorage) — ver utils/voz.js
  const [vozActivada, setVozActivada] = useState(() => voz.activa());
  const [mensajeCompleto, setMensajeCompleto] = useState(null);

  // Handlers de apertura/cierre con History API (botón atrás del teléfono cierra el modal)
  const abrirModalLista = () => {
    window.history.pushState({ modal: 'burbujaLista' }, '');
    setModalAbierto(true);
  };

  // Cierre animado (sin tocar history — usado por popstate y como helper interno)
  const cerrarModalListaAnimado = () => {
    setModalCerrando(true);
    setTimeout(() => {
      setModalAbierto(false);
      setModalCerrando(false);
    }, 300); // coincide con duración de slideOutRight en CSS
  };

  // Handler público — dispara history.back() para que popstate ejecute el cierre animado
  const cerrarModalLista = () => {
    if (window.history.state?.modal === 'burbujaLista') {
      window.history.back();
    } else {
      cerrarModalListaAnimado();
    }
  };

  const abrirMensajeCompleto = (notif) => {
    window.history.pushState({ modal: 'burbujaMensajeCompleto' }, '');
    setMensajeCompleto(notif);
  };

  // Igual que cerrarModalLista: si dejó su entrada en el historial, se cierra con history.back() y el
  // popstate lo apaga. Apagarlo ANTES del back (como estaba) hacía que el popstate llegara con el mensaje
  // ya cerrado y cerrara también la lista de notificaciones (Toto, 08/10/2026).
  const cerrarMensajeCompleto = () => {
    if (window.history.state?.modal === 'burbujaMensajeCompleto') {
      window.history.back();
    } else {
      setMensajeCompleto(null);
    }
  };

  // popstate: cierra primero el modal de mensaje completo, después el de lista (con animación)
  useEffect(() => {
    const handlePopState = () => {
      if (mensajeCompleto) {
        setMensajeCompleto(null);
      } else if (modalAbierto && !modalCerrando) {
        cerrarModalListaAnimado();
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [modalAbierto, modalCerrando, mensajeCompleto]);

  // Abre la tarea referenciada por una notificación y la marca como leída.
  const irATarea = (notif) => {
    const tareaId = notif.datosAdicionales?.tareaId || notif.referenciaId;
    if (!tareaId) return;
    notificationService.marcarComoLeida(notif.id);
    // Limpiar history stack si hay modales abiertos antes de navegar
    if (mensajeCompleto && window.history.state?.modal === 'burbujaMensajeCompleto') {
      window.history.back();
    }
    if (modalAbierto && window.history.state?.modal === 'burbujaLista') {
      window.history.back();
    }
    setMensajeCompleto(null);
    setModalAbierto(false);
    navigate(`/tareas?tab=mis-tareas&id=${tareaId}`);
  };

  // Abre la conversación escalada por la IA y marca la notificación como leída.
  // El panel de Conversaciones IA selecciona el chat con el parámetro `convId`.
  const irAlChat = (notif) => {
    const convId = notif.datosAdicionales?.convId || notif.referenciaId;
    if (!convId) return;
    notificationService.marcarComoLeida(notif.id);
    if (mensajeCompleto && window.history.state?.modal === 'burbujaMensajeCompleto') {
      window.history.back();
    }
    if (modalAbierto && window.history.state?.modal === 'burbujaLista') {
      window.history.back();
    }
    setMensajeCompleto(null);
    setModalAbierto(false);
    navigate(`/conversaciones-ia?convId=${encodeURIComponent(convId)}`);
  };

  // Abre en Lumi la conversación que dejó el resumen de la mañana (referenciaTipo 'resumen_lumi', 23/10/2026; Layout escucha
  // el evento y abre el panel). 🔴 Primero se cierra la campanita y RECIÉN cuando el historial terminó de volver se abre
  // Lumi (ESTETICA §0.19, nunca dos modales pegados): si no, el "atrás" de la campanita le llegaría al panel y lo cerraría.
  const irALumi = (notif) => {
    const conversacionId = notif.datosAdicionales?.conversacionId || notif.referenciaId;
    if (!conversacionId) return;
    notificationService.marcarComoLeida(notif.id);
    const abrirLumi = () => window.dispatchEvent(new CustomEvent('lumi:abrir-conversacion', { detail: { id: conversacionId } }));
    const modal = window.history.state?.modal;
    const entradas = modal === 'burbujaMensajeCompleto' ? 2 : modal === 'burbujaLista' ? 1 : 0;
    setMensajeCompleto(null);
    setModalAbierto(false);
    if (!entradas) {
      abrirLumi();
      return;
    }
    let listo = false;
    const seguir = () => {
      if (listo) return;
      listo = true;
      window.removeEventListener('popstate', seguir);
      setTimeout(abrirLumi, 0);
    };
    window.addEventListener('popstate', seguir);
    setTimeout(seguir, 700); // por si el navegador no avisa la vuelta
    window.history.go(-entradas);
  };

  // Refs para valores usados dentro del listener — evita recrear el listener en cada cambio
  const sonidoActivadoRef = useRef(false);
  const notificacionesAnterioresRef = useRef(0);
  // Primera carga: NO leer en voz las notificaciones que ya estaban pendientes,
  // solo las que lleguen de ahí en adelante.
  const primeraCargaRef = useRef(true);

  // Sincronizar ref de sonido cuando el usuario lo activa/desactiva
  useEffect(() => {
    sonidoActivadoRef.current = sonidoActivado;
  }, [sonidoActivado]);

  // Al desmontar (cambio de pantalla / logout), cortar cualquier lectura en curso
  useEffect(() => () => voz.callar(), []);


  // Listener creado UNA SOLA VEZ al montar el componente
  useEffect(() => {
    if (!auth.currentUser?.email) return;

    const unsubscribe = notificationService.escucharNotificaciones(
      auth.currentUser.email,
      (nuevasNotificaciones) => {
        setNotificaciones(nuevasNotificaciones);

        const cuantasNuevas = nuevasNotificaciones.length - notificacionesAnterioresRef.current;

        // Reproducir sonido si hay nuevas notificaciones
        if (sonidoActivadoRef.current && cuantasNuevas > 0) {
          notificationService.reproducirSonido();
        }

        // Leerlas en voz alta (si el colaborador tiene la voz encendida).
        // La preferencia se consulta en el momento (vive en localStorage), por eso
        // no hace falta un ref propio ni recrear el listener.
        if (!primeraCargaRef.current && cuantasNuevas > 0 && voz.activa()) {
          if (cuantasNuevas === 1) {
            const n = nuevasNotificaciones[0]; // la más reciente (orden desc)
            // Separado con comas (no puntos): las pausas de fin de oración son
            // donde más voces del sistema recortan el arranque de lo que sigue.
            voz.hablar(`Nueva notificación, ${n?.titulo || ''}, ${n?.mensaje || ''}`);
          } else {
            voz.hablar(`Tenés ${cuantasNuevas} notificaciones nuevas.`);
          }
        }
        primeraCargaRef.current = false;

        notificacionesAnterioresRef.current = nuevasNotificaciones.length;
      }
    );

    return () => {
      unsubscribe();
    };
  }, [auth.currentUser?.email]); // eslint-disable-line react-hooks/exhaustive-deps -- recrea el listener si cambia la cuenta (logout/login), no en cada notificación

  const formatearFecha = (fecha) => {
    if (!fecha) return '';
    
    // Si es un timestamp de Firebase
    if (fecha.seconds) {
      fecha = new Date(fecha.seconds * 1000);
    } else if (typeof fecha === 'string') {
      fecha = new Date(fecha);
    }
    
    const ahora = new Date();
    const diferencia = ahora - fecha;
    const minutos = Math.floor(diferencia / 60000);
    const horas = Math.floor(diferencia / 3600000);
    const dias = Math.floor(diferencia / 86400000);
    
    if (minutos < 1) return 'Hace un momento';
    if (minutos < 60) return `Hace ${minutos} minuto${minutos > 1 ? 's' : ''}`;
    if (horas < 24) return `Hace ${horas} hora${horas > 1 ? 's' : ''}`;
    if (dias < 7) return `Hace ${dias} día${dias > 1 ? 's' : ''}`;
    
    return fecha.toLocaleDateString('es-GT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const marcarComoLeida = async (notificacionId, event) => {
    event.stopPropagation();
    // Capturar el conteo ANTES de marcar — el state es de las pendientes
    const eraLaUltima = notificaciones.length === 1;
    await notificationService.marcarComoLeida(notificacionId);
    // Si era la última pendiente y el modal de lista está abierto, cerrarlo
    if (eraLaUltima && modalAbierto) {
      cerrarModalLista();
    }
  };

  const marcarTodasComoLeidas = async () => {
    if (!auth.currentUser?.email) return;
    await notificationService.marcarTodasComoLeidas(auth.currentUser.email);
    cerrarModalLista();
  };

  const getIconoTipo = (tipo) => {
    const mapa = {
      'info':        <span style={{color:'var(--color-info)', display:'inline-flex'}}><ChatBubbleLeftIcon className="w-5 h-5" /></span>,
      'warning':     <span style={{color:'var(--color-naranja)', display:'inline-flex'}}><ExclamationTriangleIcon className="w-5 h-5" /></span>,
      'error':       <span style={{color:'var(--color-peligro)', display:'inline-flex'}}><XCircleIcon className="w-5 h-5" /></span>,
      'success':     <span style={{color:'var(--color-exito)', display:'inline-flex'}}><CheckCircleIcon className="w-5 h-5" /></span>,
      'anulacion':   <span style={{color:'var(--color-peligro)', display:'inline-flex'}}><NoSymbolIcon className="w-5 h-5" /></span>,
      'autorizacion':<span style={{color:'var(--color-primario-fuerte)', display:'inline-flex'}}><LockClosedIcon className="w-5 h-5" /></span>,
      'cierre':      <span style={{color:'var(--color-primario)', display:'inline-flex'}}><LockClosedIcon className="w-5 h-5" /></span>,
      'tarea':       <span style={{color:'var(--color-exito)', display:'inline-flex'}}><ClipboardDocumentListIcon className="w-5 h-5" /></span>,
      'devolucion':  <span style={{color:'var(--color-naranja)', display:'inline-flex'}}><ArrowUturnLeftIcon className="w-5 h-5" /></span>,
    };
    return mapa[tipo] || <span style={{color:'var(--color-primario)', display:'inline-flex'}}><MegaphoneIcon className="w-5 h-5" /></span>;
  };

  const getColorPrioridad = (prioridad) => {
    const colores = {
      'urgente': 'var(--color-peligro)',
      'alta': 'var(--color-naranja)',
      'normal': 'var(--color-info)',
      'baja': 'var(--color-neutro-solido)'
    };
    return colores[prioridad] || 'var(--color-info)';
  };

  if (notificaciones.length === 0 && !modalAbierto) {
    return null;
  }

  return (
    <>
      {/* Burbuja flotante */}
      <div 
        className="burbuja-notificaciones"
        onClick={abrirModalLista}
        style={{
          position: 'fixed',
          top: '70px',
          right: '20px',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: 'var(--color-primario)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
          zIndex: 9999,
          animation: notificaciones.length > 0 ? 'pulse 2s infinite' : 'none',
          transition: 'transform 0.3s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}><BellIcon className="w-7 h-7" /></div>
          {notificaciones.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '-5px',
              right: '-5px',
              background: 'var(--color-peligro)',
              borderRadius: '50%',
              width: '25px',
              height: '25px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 'bold',
              border: '2px solid white'
            }}>
              {notificaciones.length > 99 ? '99+' : notificaciones.length}
            </div>
          )}
        </div>
      </div>

      {/* Modal de notificaciones */}
      {modalAbierto && (
        <div
          className={`modal-overlay${modalCerrando ? ' cerrando' : ''}`}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) cerrarModalLista();
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 10000,
            display: 'flex',
            alignItems: window.innerWidth <= 768 ? 'stretch' : 'flex-start',
            justifyContent: window.innerWidth <= 768 ? 'stretch' : 'flex-end',
            padding: window.innerWidth <= 768 ? '0' : '20px'
          }}
        >
          <div
            className={`modal-notificaciones${modalCerrando ? ' cerrando' : ''}`}
            style={{
              background: 'var(--color-card)',
              borderRadius: window.innerWidth <= 768 ? '0' : '12px',
              width: window.innerWidth <= 768 ? '100vw' : '400px',
              maxWidth: window.innerWidth <= 768 ? '100vw' : '90vw',
              marginTop: window.innerWidth <= 768 ? '0' : '60px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
              border: '1px solid var(--color-borde-card)',
              maxHeight: window.innerWidth <= 768 ? '100vh' : '80vh',
              minHeight: window.innerWidth <= 768 ? '100vh' : 'auto',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative'
            }}
          >
            {/* X estándar del sistema (misma pieza global que los modales); blanca
                en reposo porque queda sobre el header morado */}
            <button
              className="modal-close-std"
              onClick={cerrarModalLista}
              title="Cerrar"
              aria-label="Cerrar notificaciones"
              style={{ color: 'white' }}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>

            {/* Header del modal */}
            <div style={{
              padding: '20px',
              borderBottom: '1px solid var(--color-borde)',
              background: 'var(--color-primario)',
              borderRadius: '12px 12px 0 0',
              color: 'white'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: '30px' }}>
                <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <BellIcon className="w-5 h-5" /> Notificaciones ({notificaciones.length})
                </h3>
              </div>
              
              {/* Acción arriba (ancho completo) y los dos interruptores abajo repartiéndose
                  la fila: así no queda un botón suelto con espacio muerto al lado. */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                marginTop: '10px',
                fontSize: '12px'
              }}>
                <button
                  onClick={marcarTodasComoLeidas}
                  className="btn-press"
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    color: 'white',
                    padding: '6px 10px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    width: '100%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '5px'
                  }}
                >
                  <CheckCircleIcon className="w-4 h-4" /> Marcar todas como leídas
                </button>

                {/* Interruptores (08/10/2026; antes botones "Sonido ON/OFF" y "Voz ON/OFF"). Se aplican al
                    tocarlos, como antes, y quedan guardados en este aparato. Estilos: BurbujaNotificaciones.css. */}
                {/* Los dos SIEMPRE en la misma fila (Toto, 09/10/2026: en el celular quedaban uno abajo del otro);
                    en pantallas angostas se esconde el iconito de adelante (BurbujaNotificaciones.css). */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap' }}>
                  <div className="burbuja-notif-interruptor">
                    <span className="burbuja-notif-icono" style={{ display: 'inline-flex', flexShrink: 0 }}>
                      {sonidoActivado ? <SpeakerWaveIcon className="w-4 h-4" /> : <SpeakerXMarkIcon className="w-4 h-4" />}
                    </span>
                    <Interruptor
                      valor={sonidoActivado}
                      onChange={(nuevo) => {
                        setSonidoActivado(nuevo);
                        try { localStorage.setItem('sonidoNotificaciones', nuevo ? '1' : '0'); } catch (e) { /* sin localStorage: vale por esta sesión */ }
                        // Suena al encenderlo: confirma que se escucha y, de paso, el
                        // clic es el gesto con el que el navegador habilita el audio.
                        if (nuevo) notificationService.reproducirSonido();
                      }}
                      etiqueta="Sonido de las notificaciones"
                      textoEncendido="Con sonido"
                      textoApagado="Sin sonido"
                    />
                  </div>

                  {/* Lectura en voz alta de las notificaciones nuevas (solo si el navegador la soporta) */}
                  {voz.disponible() && (
                    <div
                      className="burbuja-notif-interruptor"
                      title={vozActivada
                        ? 'Las notificaciones nuevas se leen en voz alta'
                        : 'Activar la lectura en voz alta de las notificaciones nuevas'}
                    >
                      <span className="burbuja-notif-icono" style={{ display: 'inline-flex', flexShrink: 0 }}>
                        {vozActivada ? <VozAltaIcon className="w-4 h-4" /> : <VozMudaIcon className="w-4 h-4" />}
                      </span>
                      <Interruptor
                        valor={vozActivada}
                        onChange={(nuevo) => {
                          setVozActivada(nuevo);
                          voz.setActiva(nuevo); // guarda la preferencia y confirma hablando
                        }}
                        etiqueta="Lectura en voz alta de las notificaciones"
                        textoEncendido="Con voz"
                        textoApagado="Sin voz"
                      />
                    </div>
                  )}
                </div>

              </div>
            </div>

            {/* Lista de notificaciones */}
            <div style={{ 
              flex: 1,
              overflow: 'auto',
              padding: '10px'
            }}>
              {notificaciones.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px 20px',
                  color: 'var(--color-texto-secundario)'
                }}>
                  <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'center' }}><span style={{color:'var(--color-texto-tenue)', display:'inline-flex'}}><InboxIcon className="w-16 h-16" /></span></div>
                  <p>No tienes notificaciones pendientes</p>
                </div>
              ) : (
                notificaciones.map((notif) => (
                  <div
                    key={notif.id}
                    className="notificacion-item"
                    style={{
                      background: 'var(--color-card-elevado)',
                      border: '1px solid var(--color-borde-card)',
                      borderRadius: '8px',
                      padding: '15px',
                      marginBottom: '10px',
                      cursor: 'pointer',
                      transition: 'box-shadow 0.2s, border-color 0.2s',
                      position: 'relative',
                      boxShadow: '0 2px 6px rgba(17,12,34,0.08)'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 6px 16px rgba(17,12,34,0.14)'; e.currentTarget.style.borderColor = 'var(--color-primario)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 2px 6px rgba(17,12,34,0.08)'; e.currentTarget.style.borderColor = 'var(--color-borde-card)'; }}
                  >
                    {(notif.prioridad === 'alta' || notif.prioridad === 'urgente') && (
                      <span
                        title={`Prioridad: ${notif.prioridad}`}
                        style={{
                          position: 'absolute', top: '10px', right: '10px',
                          width: '9px', height: '9px', borderRadius: '50%',
                          background: getColorPrioridad(notif.prioridad)
                        }}
                      />
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        {notif.usuarioOrigen && (
                          <div style={{ fontSize: '11px', color: 'var(--color-primario-fuerte)', marginBottom: '5px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ color: 'var(--color-primario-fuerte)', display: 'inline-flex' }}><UserIcon className="w-3 h-3" /></span>
                            De: <strong>{notif.usuarioOrigen}</strong>
                          </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                          {getIconoTipo(notif.tipo)}
                          <strong style={{ fontSize: '14px' }}>{notif.titulo}</strong>
                        </div>
                        
                        <p style={{ 
  margin: '5px 0',
  fontSize: '13px',
  color: 'var(--color-texto-secundario)',
  whiteSpace: 'pre-wrap'
}}>
  {notif.mensaje.length > 300 
    ? `${notif.mensaje.substring(0, 300)}...` 
    : notif.mensaje}
</p>
{notif.mensaje.length > 300 && (
  <button
    onClick={(e) => {
      e.stopPropagation();
      abrirMensajeCompleto(notif);
    }}
    className="btn-press hover-bright"
    style={{
      marginTop: '8px',
      width: '100%',
      padding: '8px',
      background: 'var(--color-primario)',
      border: 'none',
      borderRadius: '5px',
      color: 'white',
      fontSize: '11px',
      fontWeight: 'bold',
      cursor: 'pointer',
      transition: 'transform 0.2s'
    }}
    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
  >
    <span style={{display:'inline-flex', alignItems:'center', justifyContent:'center', gap:'5px'}}><BookOpenIcon className="w-4 h-4" /> Ver mensaje completo</span>
  </button>
)}
                        
                        <div style={{ marginTop: '10px' }}>
                          <span style={{ fontSize: '11px', color: 'var(--color-texto-secundario)' }}>
                            {formatearFecha(notif.fechaCreacion)}
                          </span>
                        </div>

                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginTop: '8px'
                        }}>
                          <div>
                            {notif.moduloOrigen ? (
                              <span style={{
                                fontSize: '10px',
                                background: 'var(--color-borde)',
                                padding: '2px 6px',
                                borderRadius: '3px',
                                display: 'inline-flex',
                                alignItems: 'center'
                              }}>
                                <span style={{display:'inline-flex', alignItems:'center', gap:'3px'}}><span style={{color:'var(--color-primario-fuerte)', display:'inline-flex'}}><MapPinIcon className="w-3 h-3" /></span>{formatearModulo(notif.moduloOrigen)}</span>
                              </span>
                            ) : <span />}
                          </div>

                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                            {notif.referenciaTipo === 'tarea' && (notif.datosAdicionales?.tareaId || notif.referenciaId) && puedeAbrir('tareas') && (
                              <button
                                onClick={(e) => { e.stopPropagation(); irATarea(notif); }}
                                className="btn-press hover-bright"
                                style={{
                                  background: 'var(--color-primario)', color: 'white', border: 'none',
                                  padding: '3px 10px', borderRadius: '3px',
                                  fontSize: '11px', cursor: 'pointer',
                                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                                  flexShrink: 0
                                }}
                                title="Ir a la tarea"
                              >
                                <ClipboardDocumentListIcon className="w-3 h-3" /> Ver tarea
                              </button>
                            )}
                            {notif.referenciaTipo === 'escalado_chatbot' && (notif.datosAdicionales?.convId || notif.referenciaId) && puedeAbrir('conversaciones-ia') && (
                              <button
                                onClick={(e) => { e.stopPropagation(); irAlChat(notif); }}
                                className="btn-press hover-bright"
                                style={{
                                  background: 'var(--color-primario)', color: 'white', border: 'none',
                                  padding: '3px 10px', borderRadius: '3px',
                                  fontSize: '11px', cursor: 'pointer',
                                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                                  flexShrink: 0
                                }}
                                title="Abrir la conversación en Conversaciones IA"
                              >
                                <ChatBubbleLeftIcon className="w-3 h-3" /> Ver chat
                              </button>
                            )}
                            {notif.referenciaTipo === 'resumen_lumi' && (notif.datosAdicionales?.conversacionId || notif.referenciaId) && (
                              <button
                                onClick={(e) => { e.stopPropagation(); irALumi(notif); }}
                                className="btn-press hover-bright"
                                style={{
                                  background: 'var(--color-primario)', color: 'white', border: 'none',
                                  padding: '3px 10px', borderRadius: '3px',
                                  fontSize: '11px', cursor: 'pointer',
                                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                                  flexShrink: 0
                                }}
                                title="Abrir el resumen completo en Lumi (con tablas y gráficas; ahí le podés preguntar)"
                              >
                                <SparklesIcon className="w-3 h-3" /> Abrir en Lumi
                              </button>
                            )}
                            <button
                              onClick={(e) => marcarComoLeida(notif.id, e)}
                              className="btn-press hover-bright"
                              style={{
                                background: 'var(--color-primario)',
                                color: 'white',
                                border: 'none',
                                padding: '3px 10px',
                                borderRadius: '3px',
                                fontSize: '11px',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                flexShrink: 0
                              }}
                            >
                              <CheckIcon className="w-3 h-3" /> Marcar como leída
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de mensaje completo */}
      {mensajeCompleto && (
        <div
          className="modal-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) cerrarMensajeCompleto();
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            zIndex: 10001,
            display: 'flex',
            alignItems: window.innerWidth <= 768 ? 'stretch' : 'center',
            justifyContent: window.innerWidth <= 768 ? 'stretch' : 'center',
            padding: window.innerWidth <= 768 ? '0' : '20px'
          }}
        >
          <div
            style={{
              background: 'var(--color-card)',
              borderRadius: window.innerWidth <= 768 ? '0' : '12px',
              width: window.innerWidth <= 768 ? '100vw' : '500px',
              maxWidth: window.innerWidth <= 768 ? '100vw' : '90vw',
              maxHeight: window.innerWidth <= 768 ? '100vh' : '80vh',
              minHeight: window.innerWidth <= 768 ? '100vh' : 'auto',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
              border: '1px solid var(--color-borde-card)',
              position: 'relative'
            }}
          >
            {/* X estándar del sistema (global); blanca en reposo sobre header morado */}
            <button
              className="modal-close-std"
              onClick={cerrarMensajeCompleto}
              title="Cerrar"
              aria-label="Cerrar mensaje"
              style={{ color: 'white' }}
            >
              <XMarkIcon className="w-5 h-5" />
            </button>

            {/* Header */}
            <div style={{
              padding: '20px',
              paddingRight: '50px',
              borderBottom: '1px solid var(--color-borde)',
              background: 'var(--color-primario)',
              borderRadius: '12px 12px 0 0',
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {getIconoTipo(mensajeCompleto.tipo)}
                <h3 style={{ margin: 0, fontSize: '18px' }}>{mensajeCompleto.titulo}</h3>
              </div>
            </div>

            {/* Contenido */}
            <div style={{
              flex: 1,
              overflow: 'auto',
              padding: '20px'
            }}>
              {/* Información de la notificación */}
              <div style={{
                background: 'var(--color-fondo)',
                padding: '12px',
                borderRadius: '5px',
                marginBottom: '15px',
                fontSize: '12px'
              }}>
                <div style={{ marginBottom: '8px' }}>
                  <strong>Enviado por:</strong> {mensajeCompleto.usuarioOrigen}
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <strong>Fecha:</strong> {formatearFecha(mensajeCompleto.fechaCreacion)}
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <strong>Módulo:</strong> {formatearModulo(mensajeCompleto.moduloOrigen)}
                </div>
                <div>
                  <strong>Prioridad:</strong>{' '}
                  <span 
                    style={{
                      background: getColorPrioridad(mensajeCompleto.prioridad),
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: '3px',
                      fontSize: '11px',
                      fontWeight: 'bold'
                    }}
                  >
                    {mensajeCompleto.prioridad}
                  </span>
                </div>
              </div>

              {/* Mensaje completo — si la notificación trae detalle estructurado se
                  muestra como LISTADO agrupado (acá el usuario pidió el mensaje
                  "completo": un párrafo recortado con "y N más" no lo es). */}
              <div style={{
                background: 'var(--color-card)',
                border: '2px solid var(--color-borde)',
                borderRadius: '8px',
                padding: '15px',
                // `pre-wrap` respeta los espacios del texto plano, pero descuadraría
                // el listado: solo se aplica cuando de verdad hay texto plano.
                whiteSpace: tieneDetalleListado(mensajeCompleto) ? 'normal' : 'pre-wrap',
                lineHeight: '1.6',
                fontSize: '14px',
                color: 'var(--color-texto)',
                maxHeight: tieneDetalleListado(mensajeCompleto) ? '55vh' : '400px',
                overflowY: 'auto'
              }}>
                {tieneDetalleListado(mensajeCompleto)
                  ? <DetalleNotificacion notificacion={mensajeCompleto} />
                  : mensajeCompleto.mensaje}
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '15px 20px',
              borderTop: '1px solid var(--color-borde)',
              display: 'flex',
              gap: '10px',
              justifyContent: 'flex-end',
              flexWrap: 'wrap'
            }}>
              {mensajeCompleto.referenciaTipo === 'resumen_lumi' && (mensajeCompleto.datosAdicionales?.conversacionId || mensajeCompleto.referenciaId) && (
                <button
                  onClick={() => irALumi(mensajeCompleto)}
                  className="btn-press hover-bright"
                  style={{
                    background: 'var(--color-primario)',
                    color: 'white',
                    border: 'none',
                    padding: '8px 20px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}
                >
                  <SparklesIcon className="w-4 h-4" /> Abrir en Lumi
                </button>
              )}
              {!mensajeCompleto.leida && (
                <button
                  onClick={(e) => {
                    marcarComoLeida(mensajeCompleto.id, e);
                    cerrarMensajeCompleto();
                  }}
                  className="btn-press hover-bright"
                  style={{
                    background: 'var(--color-primario)',
                    color: 'white',
                    border: 'none',
                    padding: '8px 20px',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 'bold',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}
                >
                  <CheckIcon className="w-4 h-4" /> Marcar como leída
                </button>
              )}
              <button
                onClick={cerrarMensajeCompleto}
                className="btn-press hover-bright"
                style={{
                  background: 'var(--color-neutro-solido)',
                  color: 'white',
                  border: 'none',
                  padding: '8px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 'bold'
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default BurbujaNotificaciones;